import React from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
export function Overview() {
  return <div className="animate-in fade-in duration-300" data-id="element-118">
      <Card id="overview" data-id="element-119">
        <Badge data-id="element-120">Overview</Badge>
        <h2 className="text-xl font-bold text-[#8B1538] mb-3 mt-2" data-id="element-121">
          What is Jin's Three Needles?
        </h2>
        <div className="space-y-4 text-[0.95rem] text-gray-700 leading-relaxed" data-id="element-122">
          <p data-id="element-123">
            Jin's Three Needles is a Lingnan-style acupuncture system developed
            by Professor Jin Rui at Guangzhou University of Chinese Medicine,
            known for using small, highly focused three-point combinations to
            achieve strong clinical effects.
          </p>
          <p data-id="element-124">
            The method emphasizes precision over quantity so that each triad of
            points directs qi clearly to restore balance in both acute and
            chronic disorders. Unlike systems that use many needles, Jin's
            approach relies on the synergistic effect of specific point triads.
          </p>
        </div>
      </Card>

      {/* Why Three Needles - Featured Section */}
      <div className="bg-[#8B1538] rounded-lg p-6 md:p-8 mb-4 text-white" data-id="element-125">
        <h2 className="text-2xl md:text-[1.75rem] font-light mb-5 text-[#FECACA]" data-id="element-126">
          Why Three Needles?
        </h2>
        <div className="space-y-5 text-[0.95rem] leading-relaxed text-[#FEE2E2]" data-id="element-127">
          <p data-id="element-128">
            Dr. Jin believed that true acupuncture lies in{' '}
            <strong className="text-white font-semibold" data-id="element-129">
              precision, not quantity
            </strong>
            . Decades of clinical research confirmed that{' '}
            <strong className="text-white font-semibold" data-id="element-130">
              three thoughtfully-chosen points
            </strong>{' '}
            can achieve powerful, lasting results across a wide range of
            conditions.
          </p>
          <p data-id="element-131">
            Beyond technique, the number three holds deep symbolic meaning. In
            the{' '}
            <strong className="text-white font-semibold" data-id="element-132">Bagua system</strong>,
            it aligns with the{' '}
            <strong className="text-white font-semibold" data-id="element-133">
              eastern direction
            </strong>{' '}
            and the{' '}
            <strong className="text-white font-semibold" data-id="element-134">wood element</strong>,
            representing the source of growth and flowing qi. Each set of three
            needles embodies this{' '}
            <strong className="text-white font-semibold" data-id="element-135">
              continuous movement of life energy
            </strong>
            , guiding healing from within.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-136">
        <Card className="h-full" data-id="element-137">
          <h3 className="font-bold text-[#8B1538] mb-2" data-id="element-138">Core Philosophy</h3>
          <p className="text-sm text-gray-600" data-id="element-139">
            "Three needles to settle the spirit, three needles to calm the
            wind." The system is built on the TCM principle that three gives
            birth to all things, representing a complete cycle of Qi.
          </p>
        </Card>
        <Card className="h-full" data-id="element-140">
          <h3 className="font-bold text-[#8B1538] mb-2" data-id="element-141">
            Clinical Application
          </h3>
          <p className="text-sm text-gray-600" data-id="element-142">
            Widely used for neurological conditions (stroke, autism, cerebral
            palsy), pain management, and internal medicine disorders with clear
            pattern differentiation.
          </p>
        </Card>
      </div>
    </div>;
}
export function Technique() {
  return <div className="animate-in fade-in duration-300" data-id="element-143">
      {/* Distinctive Needling Techniques Header */}
      <Card id="technique" className="text-center border-b-4 border-[#8B1538]" data-id="element-144">
        <h2 className="text-2xl md:text-[1.75rem] font-light text-[#8B1538] mb-4" data-id="element-145">
          Distinctive Needling Techniques
        </h2>
        <p className="text-[0.95rem] text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-146">
          Dr. Jin Rui's acupuncture method is characterized by precision,
          intention, and a strong emphasis on practitioner–patient interaction.
          His approach to needle insertion, manipulation, and withdrawal
          reflects a commitment to traditional techniques that enhance
          therapeutic effectiveness while minimizing discomfort and side
          effects.
        </p>
      </Card>

      {/* Three Column Technique Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-147">
        {/* Insertion */}
        <Card className="h-full" data-id="element-148">
          <h3 className="text-sm uppercase tracking-widest text-[#8B1538] font-medium mb-3 text-center" data-id="element-149">
            Insertion
          </h3>
          <div className="text-[0.9rem] text-gray-700 leading-relaxed" data-id="element-150">
            <p data-id="element-151">
              Dr. Jin Rui practices a{' '}
              <strong className="font-semibold text-gray-900" data-id="element-152">
                slow, deliberate insertion technique
              </strong>
              , directing his <em data-id="element-153">yi nian</em> (intention) precisely toward each
              point. This mindful approach focuses the flow of qi, enhances the{' '}
              <em data-id="element-154">de qi</em> sensation, and allows for exceptional accuracy in
              point location.
            </p>
            <p className="mt-3" data-id="element-155">
              Compared to the rapid "flying needle" technique, Dr. Jin believes
              that this controlled, focused needling style supports more
              targeted clinical outcomes.
            </p>
          </div>
        </Card>

        {/* Manipulation */}
        <Card className="h-full" data-id="element-156">
          <h3 className="text-sm uppercase tracking-widest text-[#8B1538] font-medium mb-3 text-center" data-id="element-157">
            Manipulation
          </h3>
          <div className="text-[0.9rem] text-gray-700 leading-relaxed" data-id="element-158">
            <p data-id="element-159">
              Dr. Jin exclusively uses{' '}
              <strong className="font-semibold text-gray-900" data-id="element-160">
                manual needle manipulation, rotating the needles every 10
                minutes, three to five times in each direction
              </strong>
              . He believes this hands-on approach deepens the therapeutic
              effect by keeping both practitioner and patient engaged.
            </p>
            <p className="mt-3" data-id="element-161">
              Though he recognizes the benefits of electro-acupuncture, he
              considers acupuncture a form of communication best achieved
              through direct manual techniques.
            </p>
          </div>
        </Card>

        {/* Withdrawal */}
        <Card className="h-full" data-id="element-162">
          <h3 className="text-sm uppercase tracking-widest text-[#8B1538] font-medium mb-3 text-center" data-id="element-163">
            Withdrawal
          </h3>
          <div className="text-[0.9rem] text-gray-700 leading-relaxed" data-id="element-164">
            <p data-id="element-165">
              To minimize post-treatment swelling, which is especially common
              when using scalp points in children, Dr. Jin{' '}
              <strong className="font-semibold text-gray-900" data-id="element-166">
                withdraws the needle at the same angle as the insertion angle
                and immediately applies firm pressure
              </strong>{' '}
              to the site after needle removal.
            </p>
            <p className="mt-3" data-id="element-167">
              This technique helps prevent bleeding and reduces the likelihood
              of localized swelling.
            </p>
          </div>
        </Card>
      </div>

      {/* Original Content */}
      <Card data-id="element-168">
        <Badge data-id="element-169">Technique</Badge>
        <h2 className="text-xl font-bold text-[#8B1538] mb-3 mt-2" data-id="element-170">
          Needle Insertion & Intention
        </h2>
        <div className="space-y-4 text-[0.95rem] text-gray-700 leading-relaxed" data-id="element-171">
          <p data-id="element-172">
            Professor Jin applies a slow, deliberate insertion method, aligning
            intention closely with each point so qi is guided precisely. This is
            not just mechanical insertion; it requires the practitioner to focus
            their Shen (spirit) on the needle tip.
          </p>
          <p data-id="element-173">
            This deepens de qi (arrival of qi), refines point location, and
            strengthens practitioner–patient interaction compared with quick
            "flying needle" styles. The patient should feel a distinct sensation
            that travels or spreads.
          </p>
        </div>
      </Card>

      <Card data-id="element-174">
        <h2 className="text-[1.1rem] font-bold text-[#8B1538] mb-3" data-id="element-175">
          Manipulation & Withdrawal
        </h2>
        <div className="space-y-4 text-[0.95rem] text-gray-700 leading-relaxed" data-id="element-176">
          <p data-id="element-177">
            Needles are stimulated manually every 10 minutes with 3–5 clockwise
            and counter‑clockwise rotations. The manipulation should be even and
            rhythmic, neither too forceful nor too weak.
          </p>
          <div className="bg-[#FEF2F2] p-4 rounded-md border border-[#FECACA]" data-id="element-178">
            <h4 className="font-semibold text-[#8B1538] text-sm mb-1" data-id="element-179">
              Safety Note
            </h4>
            <p className="text-sm text-[#991B1B]" data-id="element-180">
              Withdraw along the original angle with firm post‑removal pressure
              to reduce bleeding and swelling, especially for scalp points and
              points near vessels.
            </p>
          </div>
        </div>
      </Card>
    </div>;
}
export function AboutJin() {
  return <div className="animate-in fade-in duration-300" data-id="element-181">
      <Card id="about-jin" data-id="element-182">
        <Badge data-id="element-183">Biography</Badge>
        <h2 className="text-xl font-bold text-[#8B1538] mb-3 mt-2" data-id="element-184">
          Professor Jin Rui
        </h2>
        <div className="flex flex-col md:flex-row gap-6" data-id="element-185">
          <div className="flex-1 space-y-4 text-[0.95rem] text-gray-700 leading-relaxed" data-id="element-186">
            <p data-id="element-187">
              Professor Jin Rui (1932–2010) was a distinguished professor at
              Guangzhou University of Chinese Medicine. He systematized Jin's
              Three Needles and developed many condition‑specific triads over
              more than five decades of clinical work.
            </p>
            <p data-id="element-188">
              His work was instrumental in modernizing acupuncture education in
              southern China. He was known for his rigorous approach to point
              location and his deep understanding of classical texts, which he
              combined with modern anatomical knowledge.
            </p>
            <p data-id="element-189">
              The "Three Needles" system (San Zhen) was the culmination of his
              life's work, distilling complex treatments into elegant,
              reproducible, and highly effective protocols.
            </p>
          </div>
          <div className="md:w-1/3 bg-[#FAF8F6] rounded-lg flex items-center justify-center p-8 text-gray-400 italic text-sm border border-gray-200" data-id="element-190">
            [Portrait of Professor Jin Rui]
          </div>
        </div>
      </Card>

      <Card data-id="element-191">
        <h3 className="font-bold text-[#8B1538] mb-3" data-id="element-192">Legacy</h3>
        <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700" data-id="element-193">
          <li data-id="element-194">Founded the Jin's Three Needles Research Center</li>
          <li data-id="element-195">Authored multiple textbooks on Lingnan acupuncture</li>
          <li data-id="element-196">Trained thousands of students who now practice globally</li>
          <li data-id="element-197">Recognized as a Master of Traditional Chinese Medicine</li>
        </ul>
      </Card>
    </div>;
}